<template>
  <nav
    v-if="pageCount > 1"
    class="play-pagination"
    role="navigation"
  >
    <ul class="play-pagination__list">
      <!-- prev -->
      <li>
        <button
          type="button"
          class="play-pagination__btn play-pagination__btn--nav"
          :disabled="value <= 1"
          aria-label="Previous page"
          @click="go(value - 1)"
        >
          <slot name="prev-text">
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </slot>
        </button>
      </li>

      <!-- pages -->
      <li
        v-for="(item, i) in items"
        :key="i"
      >
        <span
          v-if="item.ellipsis"
          class="play-pagination__ellipsis"
        >…</span>
        <button
          v-else
          type="button"
          class="play-pagination__btn"
          :class="{ 'is-active': item.page === value }"
          :aria-current="item.page === value ? 'page' : null"
          @click="go(item.page)"
        >
          {{ item.page }}
        </button>
      </li>

      <!-- next -->
      <li>
        <button
          type="button"
          class="play-pagination__btn play-pagination__btn--nav"
          :disabled="value >= pageCount"
          aria-label="Next page"
          @click="go(value + 1)"
        >
          <slot name="next-text">
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </slot>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
/**
 * PlayPagination — page navigator. Replaces bootstrap-vue's <b-pagination>.
 * v-model holds the current page (1-based); also emits `change` on click.
 * Mirrors the b-pagination props used in the app: total-rows, per-page,
 * limit, first-number, last-number, and the prev-text / next-text slots.
 */
export default {
  name: 'PlayPagination',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Number,
      default: 1,
    },
    totalRows: {
      type: [Number, String],
      default: 0,
    },
    perPage: {
      type: [Number, String],
      default: 20,
    },
    /** max page-number buttons in the moving window */
    limit: {
      type: [Number, String],
      default: 5,
    },
    /** always render page 1 (with an ellipsis gap) */
    firstNumber: {
      type: Boolean,
      default: false,
    },
    /** always render the last page (with an ellipsis gap) */
    lastNumber: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    pageCount() {
      const per = Number(this.perPage) || 1
      return Math.max(1, Math.ceil(Number(this.totalRows) / per))
    },
    items() {
      const count = this.pageCount
      const limit = Math.max(1, Number(this.limit))
      let start = Math.max(1, this.value - Math.floor(limit / 2))
      let end = start + limit - 1
      if (end > count) {
        end = count
        start = Math.max(1, end - limit + 1)
      }

      const list = []
      if (this.firstNumber && start > 1) {
        list.push({ page: 1 })
        if (start > 2) list.push({ ellipsis: true })
      }
      for (let p = start; p <= end; p += 1) list.push({ page: p })
      if (this.lastNumber && end < count) {
        if (end < count - 1) list.push({ ellipsis: true })
        list.push({ page: count })
      }
      return list
    },
  },
  methods: {
    go(page) {
      const p = Math.min(this.pageCount, Math.max(1, page))
      if (p === this.value) return
      this.$emit('input', p)
      this.$emit('change', p)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-pagination {
  &__list {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__btn {
    display: grid;
    place-items: center;
    min-width: 38px;
    height: 38px;
    padding: 0 10px;
    border: var(--border-w) solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-body);
    cursor: pointer;
    transition:
      background var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out);

    &:hover:not(:disabled):not(.is-active) {
      background: var(--surface-sunk);
      color: var(--text-strong);
    }
    &:disabled { color: var(--text-muted); cursor: not-allowed; opacity: .5; }

    &.is-active {
      background: var(--c-secondary);
      color: var(--c-secondary-on);
    }
  }

  &__ellipsis {
    display: grid;
    place-items: center;
    min-width: 24px;
    height: 38px;
    color: var(--text-muted);
    font-weight: 700;
  }
}
</style>
